const styles = `
  .legal-page {
    padding: 8rem 2rem 5rem;
    background: var(--off-white);
  }
  .legal-content {
    max-width: 840px;
    margin: 0 auto;
    color: var(--dark);
  }
  .legal-content h1 {
    margin-bottom: 0.5rem;
    font-family: 'Cormorant Garamond', serif;
    font-size: clamp(2.5rem, 6vw, 4rem);
    font-weight: 300;
    line-height: 1.1;
  }
  .legal-updated {
    margin-bottom: 2rem;
    color: var(--mid);
    font-size: 0.82rem;
  }
  .legal-content section { margin-bottom: 2.25rem; }
  .legal-content h2 {
    margin-bottom: 0.75rem;
    font-family: 'Cormorant Garamond', serif;
    font-size: 1.75rem;
    font-weight: 400;
  }
  .legal-content h3 {
    margin: 1.25rem 0 0.5rem;
    font-size: 1rem;
    font-weight: 600;
  }
  .legal-content p,
  .legal-content li {
    color: var(--mid);
    font-size: 0.95rem;
    font-weight: 300;
    line-height: 1.75;
  }
  .legal-content ul { margin: 0.75rem 0 0 1.25rem; }
  .legal-content a { color: var(--aqua-dark); }
`;

export default function TermsPrivacyPage() {
  return (
    <>
      <style>{styles}</style>
      <main className="legal-page">
        <article className="legal-content">
          <h1>Terms of Use &amp; Privacy Notice</h1>
          <p className="legal-updated">Effective June 22, 2026</p>

          <section>
            <h2>Terms of Use</h2>
            <p>
              These terms apply when you visit aranabros.com or use its contact forms, questionnaires, galleries, and related features. By using the site, you agree to use it lawfully and in accordance with these terms.
            </p>

            <h3>Website information</h3>
            <p>
              Site content is provided for general information about Arana Brothers Construction and its services. It is not a construction proposal, estimate, warranty, engineering opinion, or contract. Project scope, pricing, scheduling, and obligations are established only through a separate written agreement signed by the appropriate parties.
            </p>

            <h3>Acceptable use</h3>
            <p>
              You may not misuse the site, attempt unauthorized access, interfere with its operation, submit unlawful or harmful material, impersonate another person, or upload files you do not have the right to provide.
            </p>

            <h3>Submitted content</h3>
            <p>
              You retain ownership of information and images you submit. You authorize Arana Brothers Construction to receive, store, review, and use that material to evaluate your inquiry, communicate with you, and provide requested services.
            </p>

            <h3>Intellectual property</h3>
            <p>
              Unless otherwise stated, the site design, written content, branding, and project media belong to Arana Brothers Construction or are used with permission. They may not be reproduced or used commercially without permission.
            </p>

            <h3>Third-party links</h3>
            <p>
              Links to third-party websites are provided for convenience. Arana Brothers Construction does not control and is not responsible for their content, availability, or privacy practices.
            </p>

            <h3>Availability and liability</h3>
            <p>
              The site is provided on an “as available” basis. While reasonable care is taken, uninterrupted operation and complete accuracy are not guaranteed. To the extent permitted by law, Arana Brothers Construction is not responsible for indirect or consequential losses arising solely from use of this informational site.
            </p>
          </section>

          <section>
            <h2>Privacy Notice</h2>

            <h3>Information collected</h3>
            <p>Information may include:</p>
            <ul>
              <li>Name, email address, telephone number, and project address.</li>
              <li>Project type, goals, budget range, timeline, property details, preferences, and messages.</li>
              <li>Plans, references, and JPG or PNG project photographs you choose to upload.</li>
              <li>Basic server records such as IP address, browser information, requested pages, and access times.</li>
            </ul>

            <h3>Local questionnaire drafts</h3>
            <p>
              The Client Remodeling Questionnaire can save an unfinished text draft in your browser’s local storage. The draft remains on that device until you clear it or successfully submit the questionnaire. Selected image files are not stored in the saved browser draft.
            </p>

            <h3>How information is used</h3>
            <ul>
              <li>To respond to inquiries and communicate about possible projects.</li>
              <li>To evaluate project scope, feasibility, scheduling, and consultation needs.</li>
              <li>To operate, maintain, secure, and improve the website and its forms.</li>
              <li>To comply with legal obligations and protect legitimate business interests.</li>
            </ul>

            <h3>Project image storage</h3>
            <p>
              Questionnaire images are stored in a submission-specific server folder. The owner receives links to those images by email. The links are not intentionally published in site navigation, but anyone who obtains a link may be able to view the associated image. Do not upload sensitive documents or images unrelated to the proposed project.
            </p>

            <h3>Where information is stored</h3>
            <p>
              Website submissions are delivered to and stored in the company’s private business email. A working copy may also be stored on a private hard drive during the estimating process. Uploaded project images are stored temporarily on the website server so they can be reviewed through the submission-specific links sent to the owner.
            </p>

            <h3>Disclosure and sharing</h3>
            <p>
              Arana Brothers Construction does not sell personal information or share it for targeted advertising. Hosting and email providers necessarily process information to operate the website and deliver submissions. If a proposed project moves forward, relevant project information and images may be shared only with vendors, suppliers, subcontractors, or other professionals involved in that build. Information may also be disclosed when required by law or reasonably necessary to protect rights, safety, and site security.
            </p>

            <h3>Retention</h3>
            <p>
              Information and images submitted for estimating are retained for up to 45 days while the proposed project is reviewed. If the project does not move forward, those estimating records are deleted after that period. If the project moves forward, information needed to perform and document the work may become part of the project record and may be used or shared as described above.
            </p>

            <h3>Security</h3>
            <p>
              Reasonable administrative and technical measures are used to protect submitted information. No internet transmission or storage system can be guaranteed completely secure.
            </p>

            <h3>Your choices</h3>
            <p>
              You may ask to review, correct, or delete information you submitted by emailing <a href="mailto:contact@aranabros.com">contact@aranabros.com</a>. Requests may require reasonable identity verification and may be limited where retention is required by law or a contractual obligation.
            </p>

            <h3>Children</h3>
            <p>
              This site is intended for adults seeking construction services and is not directed to children under 13. Arana Brothers Construction does not knowingly request personal information from children under 13.
            </p>

            <h3>Changes to this notice</h3>
            <p>
              This page may be updated as site practices or legal requirements change. The updated date at the top identifies the current version.
            </p>

            <h3>Governing law</h3>
            <p>
              These website terms are governed by applicable United States law and the law of the state connected to the services at issue, without limiting any rights that cannot lawfully be waived.
            </p>
          </section>

          <section>
            <h2>Contact</h2>
            <p>
              Questions about these terms or privacy practices may be sent to <a href="mailto:contact@aranabros.com">contact@aranabros.com</a>.
            </p>
          </section>
        </article>
      </main>
    </>
  );
}
